import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { createPayment as createHalykPayment, generateInvoiceId, loadEPayWidget } from "../../services/ePayService";
import { initFreedomPayWidget, generateOrderId, loadFreedomPayScript } from "../../services/freedomPayService";
import styled from "styled-components";
import tw from "twin.macro";
import errorMonitoring from "../../utils/errorMonitoring";
import { getPaymentErrorMessage } from "../../utils/errorMessages";
import halykLogo from "../../images/halyk.jpg";
import freedomLogo from "../../images/freedom.jpg";

// Styled Components
const Container = styled.div`
    ${tw`w-full max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg`}
`;

const BankSelector = styled.div`
    ${tw`mb-6`}
`;

const BankOption = styled.div`
    ${tw`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-300 mb-3`}
    border-color: ${props => props.selected ? '#0ABD19' : '#E5E7EB'};
    background: ${props => props.selected ? '#F0FDF4' : 'white'};

    &:hover {
        border-color: #0ABD19;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(10, 189, 25, 0.15);
    }
`;

const BankLogo = styled.div`
    ${tw`w-16 h-16 rounded-lg flex items-center justify-center mr-4 overflow-hidden`}
    background: white;
`;

const BankLogoImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const BankInfo = styled.div`
    ${tw`flex-1`}
`;

const BankName = styled.div`
    ${tw`font-bold text-lg`}
    color: ${props => props.selected ? '#0ABD19' : '#111827'};
`;

const BankDescription = styled.div`
    ${tw`text-sm text-gray-600`}
`;

const PaymentButton = styled.button`
    ${tw`w-full px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300`}
    background: ${props => props.disabled ? '#94A3B8' : '#0ABD19'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

    &:hover {
        background: ${props => props.disabled ? '#94A3B8' : '#089714'};
        transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
        box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(10, 189, 25, 0.3)'};
    }

    &:active {
        transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
    }
`;

const LoadingSpinner = styled.div`
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.6s linear infinite;
    margin-right: 8px;

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
`;

const AmountDisplay = styled.div`
    ${tw`text-center mb-6 p-4 rounded-lg`}
    background-color: #F9FAFB;
`;

const AmountLabel = styled.div`
    ${tw`text-sm text-gray-600 mb-1`}
`;

const AmountValue = styled.div`
    ${tw`text-3xl font-bold text-gray-900`}
`;

const BANKS = {
    HALYK: {
        id: 'halyk',
        name: 'Halyk Bank',
        logo: halykLogo,
        color: '#0066CC'
    },
    FREEDOM: {
        id: 'freedom',
        name: 'Freedom Pay',
        logo: freedomLogo,
        color: '#0ABD19'
    }
};

/**
 * Компонент для выбора банка и оплаты
 *
 * Props:
 * - amount: сумма платежа (обязательно)
 * - description: описание платежа
 * - onSuccess: callback при успешной оплате
 * - onFailure: callback при ошибке
 */
export default function BankSelectionPayment({
    amount,
    description,
    onSuccess,
    onFailure
}) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [selectedBank, setSelectedBank] = useState(BANKS.HALYK.id);
    const [isLoading, setIsLoading] = useState(false);
    const [halykReady, setHalykReady] = useState(false);
    const [freedomReady, setFreedomReady] = useState(false);

    // Загружаем виджеты при монтировании
    useEffect(() => {
        // Загружаем Halyk ePay
        loadEPayWidget()
            .then(() => {
                setHalykReady(true);
                console.log('✅ Halyk ePay widget loaded');
            })
            .catch((error) => {
                console.error('❌ Error loading Halyk ePay widget:', error);
            });

        // Freedom Pay работает через redirect, скрипт не нужен
        setFreedomReady(true);
        console.log('✅ Freedom Pay ready (redirect mode)');
    }, []);

    // Валидация суммы
    useEffect(() => {
        if (amount && (isNaN(amount) || amount <= 0)) {
            console.error('Некорректная сумма платежа:', amount);
        }
    }, [amount]);

    const handleHalykPayment = async () => {
        setIsLoading(true);

        try {
            const invoiceId = generateInvoiceId();
            const paymentAmount = parseFloat(amount);

            const paymentData = {
                amount: paymentAmount,
                description: description || t('paymentForm.defaultDescription'),
                accountId: user.uid,
                customerName: user.displayName || user.email?.split('@')[0] || 'Клиент',
                customerEmail: user.email,
                invoiceId,
                successUrl: `https://www.eparcel.kz/PaymentSuccess?invoice=${invoiceId}&amount=${paymentAmount}&userId=${user.uid}&bank=halyk`,
                failureUrl: `https://www.eparcel.kz/PaymentSuccess?status=failed&invoice=${invoiceId}&bank=halyk`
            };

            console.log('💳 Creating Halyk payment:', { invoiceId, amount: paymentAmount });

            await createHalykPayment(paymentData);

            errorMonitoring.addBreadcrumb('Halyk payment redirect initiated', {
                invoiceId,
                amount,
                userId: user.uid
            });

            toast.success(t('paymentForm.redirectingHalyk'));

        } catch (error) {
            console.error('❌ Halyk payment error:', error);

            errorMonitoring.captureException(error, {
                action: 'halyk_payment_failed',
                amount,
                userId: user?.uid
            });

            const errorMessage = getPaymentErrorMessage(error?.code) || error?.message || t('paymentForm.errorHalyk');
            toast.error(errorMessage);

            if (onFailure) onFailure(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFreedomPayment = async () => {
        setIsLoading(true);

        try {
            const orderId = generateOrderId();
            const paymentAmount = parseFloat(amount);

            const paymentOptions = {
                amount: paymentAmount,
                orderId: orderId,
                description: description || t('paymentForm.defaultDescription'),
                customerEmail: user.email,
                customerPhone: user.phoneNumber || '',
                customerName: user.displayName || user.email?.split('@')[0] || 'Клиент',
                accountId: user.uid, // Добавляем user ID для callback
                onError: (error) => {
                    console.error('❌ Freedom Pay error:', error);
                    toast.error(t('paymentForm.errorFreedom'));

                    errorMonitoring.captureException(error, {
                        action: 'freedom_payment_failed',
                        amount,
                        userId: user?.uid
                    });

                    if (onFailure) onFailure(error);
                }
            };

            console.log('💳 Redirecting to Freedom Pay payment page:', { orderId, amount: paymentAmount });

            errorMonitoring.addBreadcrumb('Freedom Pay payment redirect initiated', {
                orderId,
                amount,
                userId: user.uid
            });

            toast.success(t('paymentForm.redirectingFreedom'));

            // Редирект на страницу оплаты Freedom Pay
            await initFreedomPayWidget(paymentOptions);

        } catch (error) {
            console.error('❌ Freedom Pay initialization error:', error);

            errorMonitoring.captureException(error, {
                action: 'freedom_redirect_failed',
                amount,
                userId: user?.uid
            });

            toast.error(t('paymentForm.errorInit'));
            if (onFailure) onFailure(error);
            setIsLoading(false);
        }
        // Не сбрасываем isLoading, т.к. происходит redirect
    };

    const handlePayment = async () => {
        // Валидация
        if (!amount || amount <= 0) {
            toast.error(t('paymentForm.invalidAmount'));
            return;
        }

        if (!user) {
            toast.error(t('paymentForm.notAuthenticated'));
            return;
        }

        if (selectedBank === BANKS.HALYK.id) {
            if (!halykReady) {
                toast.error(t('paymentForm.halykLoading'));
                return;
            }
            await handleHalykPayment();
        } else if (selectedBank === BANKS.FREEDOM.id) {
            // Freedom Pay временно отключен
            toast.error(t('paymentForm.freedomUnavailable'));
            return;
        }
    };

    const isDisabled = isLoading || !amount || amount <= 0 ||
                      (selectedBank === BANKS.HALYK.id && !halykReady);

    return (
        <Container>
            <AmountDisplay>
                <AmountLabel>{t('paymentForm.amountLabel')}</AmountLabel>
                <AmountValue>{Math.round(amount || 0).toLocaleString()} ₸</AmountValue>
            </AmountDisplay>

            <BankSelector>
                <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>
                    {t('paymentForm.selectMethod')}
                </h3>

                <BankOption
                    selected={selectedBank === BANKS.HALYK.id}
                    onClick={() => setSelectedBank(BANKS.HALYK.id)}
                >
                    <BankLogo>
                        <BankLogoImage src={BANKS.HALYK.logo} alt={BANKS.HALYK.name} />
                    </BankLogo>
                    <BankInfo>
                        <BankName selected={selectedBank === BANKS.HALYK.id}>
                            {BANKS.HALYK.name}
                        </BankName>
                        <BankDescription>{t('paymentForm.halykDescription')}</BankDescription>
                        {!halykReady && (
                            <div style={{ fontSize: '12px', color: '#F59E0B', marginTop: '4px' }}>
                                {t('paymentForm.loading')}
                            </div>
                        )}
                    </BankInfo>
                    {selectedBank === BANKS.HALYK.id && (
                        <div style={{ fontSize: '24px', color: '#0ABD19' }}>✓</div>
                    )}
                </BankOption>

                <BankOption
                    selected={false}
                    onClick={() => toast.error(t('paymentForm.freedomUnavailable'))}
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                    <BankLogo>
                        <BankLogoImage src={BANKS.FREEDOM.logo} alt={BANKS.FREEDOM.name} />
                    </BankLogo>
                    <BankInfo>
                        <BankName selected={false}>
                            {BANKS.FREEDOM.name}
                        </BankName>
                        <BankDescription>{t('paymentForm.freedomUnavailable')}</BankDescription>
                    </BankInfo>
                </BankOption>
            </BankSelector>

            <PaymentButton
                onClick={handlePayment}
                disabled={isDisabled}
                type="button"
            >
                {isLoading && <LoadingSpinner />}
                {isLoading ? t('paymentForm.processing') : `${t('paymentForm.payThrough')} ${selectedBank === BANKS.HALYK.id ? 'Halyk Bank' : 'Freedom Pay'}`}
            </PaymentButton>
        </Container>
    );
}
